import type { ReactNode } from "react";
import { Button, Select, vars } from "@pack-it-up/ui-web";
import type { SyncCounts, TemplateSummary } from "@pack-it-up/core";
import type { TemplateId } from "@pack-it-up/shared";

// Doc 13 §1 — the sticky header of sync mode: which template is being synced, what the
// current ticks add up to, and the two ways out. Presentational: every action is an
// event out to the page, which owns the `templateSyncStore`.

// "12 to update · 3 to add · 1 to remove · 2 conflicts", omitting the zeroes.
export const syncCountsLabel = (counts: SyncCounts): string => {
  const parts: string[] = [];
  if (counts.updates > 0) parts.push(`${counts.updates} to update`);
  if (counts.adds > 0) parts.push(`${counts.adds} to add`);
  if (counts.removes > 0) parts.push(`${counts.removes} to remove`);
  if (counts.conflicts > 0) parts.push(`${counts.conflicts} conflict${counts.conflicts === 1 ? "" : "s"}`);
  return parts.length === 0 ? "Nothing to sync yet" : parts.join(" · ");
};

// Writes the confirm button counts: everything that changes the template.
export const syncChangeCount = (counts: SyncCounts): number => counts.updates + counts.adds + counts.removes;

export interface SyncModeBarProps {
  candidates: TemplateSummary[];
  templateId: TemplateId | null;
  counts: SyncCounts;
  loading: boolean;
  applying: boolean;
  error: string | null;
  onSelectTemplate: (templateId: TemplateId) => void;
  onCancel: () => void;
  // Opens the confirm dialog — the sync itself is never one click away (Doc 13 §8).
  onReview: () => void;
}

export const SyncModeBar = ({
  candidates,
  templateId,
  counts,
  loading,
  applying,
  error,
  onSelectTemplate,
  onCancel,
  onReview,
}: SyncModeBarProps): ReactNode => {
  const changes = syncChangeCount(counts);
  return (
    <div
      role="region"
      aria-label="Sync to template"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 20,
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: vars.space.md,
        padding: `${vars.space.sm} ${vars.space.md}`,
        marginBottom: vars.space.md,
        borderRadius: vars.radius.md,
        border: `1px solid ${vars.color.accent}`,
        background: vars.color.accentSubtle,
      }}
    >
      <Select
        compact
        label="Sync to template"
        value={templateId ?? ""}
        options={[
          ...(templateId === null ? [{ value: "", label: "Choose a template…" }] : []),
          ...candidates.map((t) => ({ value: t.id as string, label: t.name })),
        ]}
        onChange={(v) => {
          if (v !== "") onSelectTemplate(v as TemplateId);
        }}
        disabled={applying}
      />
      <span style={{ fontSize: vars.font.sizeSm, color: vars.color.text, fontVariantNumeric: "tabular-nums" }}>
        {loading ? "Loading template…" : syncCountsLabel(counts)}
      </span>
      {error !== null && (
        <span role="alert" style={{ fontSize: vars.font.sizeXs, color: vars.color.danger }}>
          {error}
        </span>
      )}
      <span style={{ marginLeft: "auto", display: "flex", gap: vars.space.sm }}>
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={applying}>
          Cancel
        </Button>
        <Button size="sm" onClick={onReview} disabled={applying || loading || templateId === null || changes === 0}>
          {applying ? "Syncing…" : `Sync ${changes} change${changes === 1 ? "" : "s"}`}
        </Button>
      </span>
    </div>
  );
};
